import React from 'react';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ViewTitle from '../../../common/layout/ViewTitle';
import ReportNotFound from '../../misc/ReportNotFound';
import BackendError from '../../misc/BackendError';
import PredictionError from '../../misc/PredictionError';
import ReportRunning from '../../misc/ReportRunning';
import { PredictionStatus } from "../consts";
import AdmEvrChart from './AdmEvrChart';
import AdmDatesTable from '../adm_arrival_closure/AdmDatesTable';

const axios = require('axios').default;

function AdmEvr(props) {
  const { release, jwtToken, baseApiUri, getUrl } = props;

  // -------------------------------- State -------------------------------- //
  // ----------------------------------------------------------------------- //
  const [loadCount, setLoadCount] = React.useState(0);

  const [report, setReport] = React.useState();
  const [status, setStatus] = React.useState();
  const [timestamp, setTimestamp] = React.useState();
  const [backendError, setBackendError] = React.useState(false);
  const [reportNotFound, setReportNotFound] = React.useState(false);

  // --------------------------- Lifecycle Hooks --------------------------- //
  // ----------------------------------------------------------------------- //
  React.useEffect(() => {
    if (release) {
      setReport(undefined);
      setBackendError(false);
      setReportNotFound(false);

      const headers = { Authorization: jwtToken };

      axios.get(`${baseApiUri}${getUrl(release)}`, { headers })
        .then(response => {
          setReport(response.data);
          setStatus(response.data.status);
          setTimestamp(response.data.timestamp);

          if (response.data.status?.toUpperCase() === PredictionStatus.RUNNING.toUpperCase()) {
            setTimeout(() => setLoadCount(loadCount + 1), 2000);
          }
        })
        .catch(err => {
          if (err.response) {
            if (err.response.status === 404) {
              setReportNotFound(true);
            }
            else if (err.response.status >= 500) {
              setBackendError(true);
            }
          } else {
            setBackendError(true);
          }
        })
    }
  }, [release, getUrl, jwtToken, baseApiUri, loadCount]);

  // ------------------------------- Render -------------------------------- //
  // ----------------------------------------------------------------------- //
  if (backendError) return <BackendError />
  if (reportNotFound) return <ReportNotFound />

  if (status?.toUpperCase() === PredictionStatus.RUNNING.toUpperCase()) return <ReportRunning timestamp={timestamp} />

  // Check if we have the data structure we need
  const hasValidData = report && report.raw_response && report.raw_response.evr;
  let latestEVR = null;

  if (hasValidData && report.raw_response.evr?.cumulative?.length > 0) {
    const evrData = report.raw_response.evr.cumulative;
    const lastValid = [...evrData].reverse().find(d => d.value !== null);
    if (lastValid) {
      latestEVR = lastValid.value;
    }
  }

  const evrStatus = getEVRStatus(latestEVR);

  return (
    <React.Fragment>
      <ViewTitle title='EVR Chart' />

      {status?.toUpperCase() === PredictionStatus.ERROR.toUpperCase() && (
        <PredictionError error={report?.error} />
      )}

      {!hasValidData && report && status?.toUpperCase() !== PredictionStatus.ERROR.toUpperCase() && status?.toUpperCase() !== PredictionStatus.RUNNING.toUpperCase() && (
        <PredictionError error="Invalid or incomplete ADM data structure received from the server." />
      )}

      {
        hasValidData && (
          <Grid container spacing={1} justifyContent='center'>
            <Grid item xs={12}>
              <Paper
                style={{
                  padding: "12px",
                  backgroundColor: "#f5f7fa",
                  border: "1px solid #ddd",
                }}
              >
                <Typography variant="body1" >
                  The current EVR score is <strong>{latestEVR?.toFixed(2) ?? "N/A"}</strong>. 
                  The project is in the <strong>{evrStatus.label}</strong> zone.
                </Typography>

                <Typography variant="body1" style={{ marginTop: 6 }}>
                  The recommended action is: <strong>{evrStatus.recommendation}</strong>
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <AdmEvrChart evr={report.raw_response.evr} increment={report.increment} d0_interval={report.raw_response.d0_interval} d2_interval={report.raw_response.d2_interval} />
            </Grid>
            <Grid item xs={8} md={5}>
              <AdmDatesTable {...report} />
            </Grid>
          </Grid>
        )
      }
    </React.Fragment>
  )
  function getEVRStatus(evr) {
    if (evr === null || evr === undefined) {
      return { label: "Unknown", color: "#9e9e9e", recommendation: "N/A" };
    }

    if (evr < 0.6) {
      return { label: "Over Capacity", color: "#76a278", recommendation: "Continue balanced feature + Quality Execution."};
    }

    if (evr < 0.8) {
      return { label: "Healthy", color: "#1b5e20",recommendation: "Continue balanced feature + Quality Execution."};
    }

    if (evr < 1.00) {
      return { label: "Warning", color: "#f9a825",recommendation: "Increase bug-fix capacity; implement corrective actions to reduce incoming defects."};
    }

    return { label: "Out of Control", color: "#c62828",recommendation: "Stop new feature work; Prioritize root-cause analysis and defect burn-down."};
  }
  

}

function mapStateToProps(state) {
  return {
    release: state.common.release,
    jwtToken: state.common.jwtToken,
    baseApiUri: state.common.baseApiUri,
  };
}

export default connect(mapStateToProps)(AdmEvr)
